import React from 'react';
import { fetchTree, fetchSources, fetchSource, updateTree, createTree, fetchTaskCategories } from '../../../apis';
import { Form } from '../../form/Form';
import { get, List, Map } from 'immutable'; // bulids a definition id based on a name (similar to slugify)

var buildDefinitionId = function buildDefinitionId(text) {
  return text.trim() // Convert uppercase to lowercase
  .toLowerCase() // Replace spaces with _
  .replace(/\s+/g, '_') // Remove unwanted chars
  .replace(/[^A-Za-z0-9_]+/g, '');
};

var dataSources = function dataSources(_ref) {
  var workflowType = _ref.workflowType,
      sourceName = _ref.sourceName,
      sourceGroup = _ref.sourceGroup,
      name = _ref.name;
  return {
    workflow: {
      fn: fetchTree,
      params: name && [{
        type: workflowType || 'Tree',
        sourceName: sourceName,
        sourceGroup: sourceGroup,
        name: name,
        include: 'details,inputs,outputs,categories'
      }],
      transform: function transform(result) {
        return result.tree;
      }
    },
    selectedSource: {
      fn: fetchSource,
      params: function params(_ref2) {
        var values = _ref2.values;
        return values && values.get('sourceName') && [{
          sourceName: values.get('sourceName'),
          include: 'predefinedSourceGroups,predefinedTreeNames'
        }];
      },
      transform: function transform(result) {
        return result.source;
      }
    },
    sources: {
      fn: fetchSources,
      params: [],
      transform: function transform(result) {
        return result.sources.map(function (s) {
          return {
            label: s.name,
            value: s.name
          };
        });
      }
    },
    categories: {
      fn: fetchTaskCategories,
      params: [],
      transform: function transform(result) {
        return result.categories;
      }
    }
  };
};

var handleSubmit = function handleSubmit(_ref3) {
  var sourceName = _ref3.sourceName,
      sourceGroup = _ref3.sourceGroup,
      name = _ref3.name;
  return function (values) {
    return (name ? updateTree({
      sourceName: sourceName,
      sourceGroup: sourceGroup,
      name: name,
      tree: values.toJS()
    }) : createTree({
      tree: values.toJS()
    })).then(function (_ref4) {
      var tree = _ref4.tree,
          error = _ref4.error;

      if (error) {
        throw error.statusCode === 400 && error.message || 'There was an error saving the workflow';
      } else {
        return tree;
      }
    });
  };
};

var fields = function fields(_ref5) {
  var name = _ref5.name,
      workflowType = _ref5.workflowType;
  return function (_ref6) {
    var workflow = _ref6.workflow,
        categories = _ref6.categories;
    return (!name || workflow) && categories && [{
      name: 'sourceName',
      label: 'Source',
      type: 'select',
      required: true,
      options: function options(_ref7) {
        var sources = _ref7.sources;
        return sources;
      },
      initialValue: workflow ? workflow.get('sourceName') : workflowType === 'routines' ? '-' : '',
      helpText: 'The application that is calling and getting the results back from the workflow.'
    }, {
      name: 'sourceGroup',
      label: 'Source Group',
      type: 'text',
      required: true,
      options: function options(_ref8) {
        var selectedSource = _ref8.selectedSource;
        return selectedSource ? selectedSource.get('predefinedSourceGroups').map(function (g) {
          return Map({
            label: g,
            value: g
          });
        }) : List();
      },
      initialValue: workflow ? workflow.get('sourceGroup') : workflowType === 'routines' ? '-' : '',
      helpText: "Categorization of the workflow based on rules provided by the Source. For Request CE it's the combination of the type (submission of form), Kapp Slug and the Form Slug separated by a greater than sign ( > ). Example: Submissions > services > onboarding."
    }, {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      options: function options(_ref9) {
        var selectedSource = _ref9.selectedSource;
        return selectedSource ? selectedSource.get('predefinedTreeNames').map(function (n) {
          return Map({
            label: n,
            value: n
          });
        }) : List();
      },
      onChange: function onChange(_ref10, _ref11) {
        var values = _ref10.values;
        var setValue = _ref11.setValue;

        if (values.has('definitionId') && values.get('linked')) {
          setValue('definitionId', buildDefinitionId(values.get('name')), false);
        }
      },
      initialValue: workflow ? workflow.get('name') : '',
      helpText: 'Name is generally the event that causes the tree or routine to be run. Example: Category Update.'
    }, {
      name: 'linked',
      label: 'Linked',
      type: 'checkbox',
      "transient": true,
      initialValue: !name,
      visible: false
    }, workflowType !== 'trees' && {
      name: 'definitionId',
      label: 'Definition ID',
      type: 'text',
      enabled: name ? false : true,
      required: true,
      onChange: function onChange(_bindings, _ref12) {
        var setValue = _ref12.setValue;
        setValue('linked', false);
      },
      initialValue: get(workflow, 'definitionId', '') || '',
      helpText: 'Definition ID is generated by the system and used for API requests',
      serialize: function serialize(_ref13) {
        var values = _ref13.values;
        return name ? values.get('definitionId') : "routine_".concat(values.get('definitionId'));
      }
    }, {
      name: 'notes',
      label: 'Notes',
      type: 'text',
      required: false,
      initialValue: get(workflow, 'notes', null) === null ? '' : get(workflow, 'notes')
    }, {
      name: 'ownerEmail',
      label: 'Process Owner Email',
      type: 'text',
      required: false,
      initialValue: get(workflow, 'ownerEmail', null) === null ? '' : get(workflow, 'ownerEmail')
    }, {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [{
        label: 'Active',
        value: 'Active'
      }, {
        label: 'Inactive',
        value: 'Inactive'
      }, {
        label: 'Paused',
        value: 'Paused'
      }],
      initialValue: get(workflow, 'status', 'Active'),
      helpText: 'Determines whether the workflow is available to be called. Triggers for paused workflows will go into a Staged status'
    }, workflowType !== 'trees' && {
      name: 'categories',
      label: 'Categories',
      type: 'select-multi',
      initialValue: get(workflow, 'categories', List()).map(function (c) {
        return c.get('name');
      }),
      options: categories.sortBy(function (c) {
        return c.get('name');
      }).map(function (c) {
        return Map({
          label: c.get('name'),
          value: c.get('name')
        });
      })
    }, workflowType !== 'trees' && {
      name: 'inputs',
      label: 'Inputs',
      type: 'table',
      options: [{
        name: 'name',
        label: 'Name',
        type: 'text'
      }, {
        name: 'defaultValue',
        label: 'Default Value',
        type: 'text'
      }, {
        name: 'description',
        label: 'Description',
        type: 'text'
      }, {
        name: 'required',
        label: 'Required',
        type: 'checkbox'
      }],
      initialValue: get(workflow, 'inputs', [])
    }, workflowType !== 'trees' && {
      name: 'outputs',
      label: 'Outputs',
      type: 'table',
      options: [{
        name: 'name',
        label: 'Name',
        type: 'text'
      }, {
        name: 'description',
        label: 'Description',
        type: 'text'
      }],
      initialValue: get(workflow, 'outputs', [])
    }];
  };
};

export var WorkflowForm = function WorkflowForm(_ref14) {
  var addFields = _ref14.addFields,
      alterFields = _ref14.alterFields,
      fieldSet = _ref14.fieldSet,
      formKey = _ref14.formKey,
      components = _ref14.components,
      onSave = _ref14.onSave,
      onError = _ref14.onError,
      children = _ref14.children,
      workflowType = _ref14.workflowType,
      sourceName = _ref14.sourceName,
      sourceGroup = _ref14.sourceGroup,
      name = _ref14.name;
  return /*#__PURE__*/React.createElement(Form, {
    addFields: addFields,
    alterFields: alterFields,
    fieldSet: fieldSet,
    formKey: formKey,
    components: components,
    onSubmit: handleSubmit,
    onSave: onSave,
    onError: onError,
    dataSources: dataSources,
    fields: fields,
    formOptions: {
      workflowType: workflowType,
      sourceName: sourceName,
      sourceGroup: sourceGroup,
      name: name
    }
  }, children);
};